import { useEffect, useState } from 'react';

/**
 * SYM-6226. One clock, running from zero, for as long as it is wanted.
 *
 * The style step draws three examples at once and they all move. Three clocks
 * would be three animation loops re-rendering three subtrees on their own
 * schedules; this is one loop, and each example folds the number into its own
 * length (loopedTime in styleExamples.ts) rather than being handed a time of
 * its own.
 *
 * It is deliberately NOT useTrack1Preview. That one is a player: it has play,
 * pause, a seek and an end it stops at, because the Preview step's video is
 * something a customer drives. These examples are not driven by anybody -- they
 * loop from the moment they can, the way the clips they replace did -- so a
 * player's controls would be five things to keep working that nothing presses.
 *
 * `running` false stops the loop and leaves the number where it was, so the
 * frames freeze rather than jump back to the start.
 */
export const useLoopingClock = (running: boolean): number => {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!running) return undefined;

    let frame: number | null = null;
    // Measured against a start, never accumulated frame by frame: a tab that
    // was in the background comes back to the right moment instead of to
    // however many frames it managed to run.
    const startedAt = performance.now();

    const tick = () => {
      setSeconds((performance.now() - startedAt) / 1000);
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [running]);

  return seconds;
};

export default useLoopingClock;
